import { BUNDLE_DISCOUNT } from '@features/details/constants';
import { BundleBasketFragment, ProductType } from '@generated/gql/graphql';
import { convertValueToCurrency, isAmountCredit } from '@utils/price';

import {
  formattedBroadband,
  formattedCashbackCard,
  formattedEnergy,
  formattedInsurance,
  formattedMobile,
} from './formatter';
import { GroupedQuotes, Product } from './types';

const useBasketData = (basket: BundleBasketFragment) => {
  if (!basket)
    return {
      selectedProducts: [],
      addOns: [],
      monthlyCost: convertValueToCurrency(0),
    };

  const { energy, broadband, cashbackCard, insurance } =
    basket.selectedQuotes.reduce(
      (acc, quote) => {
        switch (quote.product.type) {
          case ProductType.Energy:
            acc.energy.push(quote);
            break;
          case ProductType.Broadband:
            acc.broadband.push(quote);
            break;
          case ProductType.CashbackCard:
            acc.cashbackCard.push(quote);
            break;
          case ProductType.Insurance:
            acc.insurance.push(quote);
            break;
        }
        return acc;
      },
      {
        energy: [],
        broadband: [],
        cashbackCard: [],
        insurance: [],
      } as GroupedQuotes
    );

  const formattedEnergyProduct = formattedEnergy(energy);
  const formattedBroadbandProduct = formattedBroadband(broadband[0]);
  const formattedCashbackCardProduct = formattedCashbackCard(cashbackCard[0]);
  const formattedMobileProduct = formattedMobile(basket.mobile ?? null);
  const formattedInsuranceProduct = formattedInsurance(insurance);

  const selectedProducts = [
    formattedEnergyProduct,
    formattedBroadbandProduct,
    formattedMobileProduct,
    formattedInsuranceProduct,
  ].filter((product): product is Product => product !== null);

  const addOns: Product[] = [];
  if (formattedCashbackCardProduct) {
    addOns.push(formattedCashbackCardProduct);
  }

  const monthlyCost = basket?.monthlyCost;
  const bundleDiscount = basket?.discounts?.find(
    (discount) => discount?.id === BUNDLE_DISCOUNT
  );

  const bundleDiscountQualified =
    bundleDiscount?.qualified && isAmountCredit(bundleDiscount.credit)
      ? bundleDiscount.credit.amount
      : null;

  // Since the backend still returns this as "qualified", we want to hide this discount
  // This benefit is being officially retired on 01 April 2025
  const updatedMonthlyCost = bundleDiscountQualified
    ? {
        ...monthlyCost,
        value: (
          Number(monthlyCost.value) + Number(bundleDiscountQualified)
        ).toString(),
      }
    : monthlyCost;

  return {
    selectedProducts,
    addOns,
    monthlyCost: updatedMonthlyCost,
  };
};

export { useBasketData };
